import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { LucideIcon } from 'lucide-react';

interface AdminCardProps {
  title?: string;
  description?: string;
  icon?: LucideIcon;
  actions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  isLoading?: boolean;
  loadingRows?: number;
  variant?: 'default' | 'outlined' | 'elevated' | 'interactive' | 'status' | 'actions' | 'glass';
  headerClassName?: string;
  contentClassName?: string;
  iconColor?: 'primary' | 'emerald' | 'purple' | 'orange' | 'indigo' | 'green';
}

export const AdminCard: React.FC<AdminCardProps> = ({
  title,
  description,
  icon: Icon,
  actions,
  children,
  className,
  isLoading = false,
  loadingRows = 3,
  variant = 'default',
  headerClassName,
  contentClassName,
  iconColor = 'primary',
}) => {
  const variantStyles = {
    default: 'border-gray-200 shadow-sm hover:shadow-md transition-all duration-200',
    outlined: 'border-2 border-gray-200 hover:border-primary-300 shadow-none transition-all duration-200',
    elevated: 'border-gray-200 shadow-lg shadow-gray-100/50 hover:shadow-xl hover:shadow-gray-200/50 transition-all duration-300',
    interactive: 'border-gray-200 shadow-sm hover:shadow-md transition-all duration-200',
    status: 'border-gray-200 shadow-sm hover:shadow-md transition-all duration-200',
    actions: 'border-gray-200 shadow-sm hover:shadow-md transition-all duration-200',
    glass: 'glass-effect card-hover shadow-lg border-white/20',
  };

  const iconColorStyles = {
    primary: 'bg-primary-100 text-primary-600',
    emerald: 'bg-emerald-100 text-emerald-600',
    purple: 'bg-purple-100 text-purple-600',
    orange: 'bg-orange-100 text-orange-600',
    indigo: 'bg-indigo-100 text-indigo-600',
    green: 'bg-green-100 text-green-600',
  };

  if (isLoading) {
    return (
      <div className={cn('bg-white rounded-xl', variantStyles[variant], className)}>
        {(title || description) && (
          <div className={cn('p-6 pb-4', headerClassName)}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 flex-1">
                {Icon && <Skeleton className="w-10 h-10 rounded-lg" />}
                <div className="space-y-2 flex-1">
                  {title && <Skeleton className="h-6 w-48" />}
                  {description && <Skeleton className="h-4 w-72" />}
                </div>
              </div>
              {actions && <Skeleton className="w-24 h-8" />}
            </div>
          </div>
        )}
        <div className={cn('px-6 pb-6', contentClassName)}>
          <div className="space-y-4">
            {Array.from({ length: loadingRows }).map((_, i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-4/5" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('bg-white rounded-xl', variantStyles[variant], className)}>
      {(title || description || actions) && (
        <div className={cn('p-6 pb-4', headerClassName)}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              {Icon && (
                <div className={cn(
                  'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0',
                  iconColorStyles[iconColor]
                )}>
                  <Icon className="w-5 h-5" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                {title && (
                  <h3 className="text-xl font-semibold text-gray-900 leading-tight">
                    {title}
                  </h3>
                )}
                {description && (
                  <p className="text-gray-600 mt-1 leading-relaxed">
                    {description}
                  </p>
                )}
              </div>
            </div>
            {actions && (
              <div className="flex items-center gap-2 flex-shrink-0">
                {actions}
              </div>
            )}
          </div>
        </div>
      )}
      <div className={cn('px-6 pb-6', contentClassName)}>
        {children}
      </div>
    </div>
  );
}; 